import React, { useState, useCallback, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import style from './ProfilePage.module.css';
import StrydCardSmall from '../../components/StrydCardSmall/StrydCardSmall';
import Tag from '../../components/Tag/Tag';
import rankData from '../../utils/ranks';
import findUserRank from '../../utils/findUserRank';
import RanksModal from '../../components/RanksModal/RanksModal';
import PrimaryButton from '../../components/PrimaryButton/PrimaryButton';
import SocialConnectionsModal from '../../components/SocialConnectionsModal/SocialConnectionsModal';
import { requestWithApiFallback } from '../../utils/apiRequest';
import { checkSubscription } from '../../utils/checkSubscription';
import AppLoader from '../../components/AppLoader/AppLoader';
import { MdExpandMore, MdExpandLess, MdCalendarMonth, MdPlayArrow, MdArrowForward } from 'react-icons/md';
import formatDisplayName from '../../utils/formatDisplayName';
import { getMealEntriesForDay } from '../../utils/mealPlan';
import VerifiedBadge from '../../components/VerifiedBadge/VerifiedBadge';
import { normalizeRoutineCreationType, normalizeUserPayload } from '../../utils/userData';

import { useAuthUser } from '../../hooks/useAuthUser';

// import {clerkClient} from '@clerk/clerk-react/server'


const ProfilePage = () => {
	const { user, isLoaded } = useAuthUser();
	const navigate = useNavigate();
	const location = useLocation();
	const API_URL = process.env.REACT_APP_API_URL;

	const [dbUser, setDbUser] = useState(null);
	const [isProfileLoading, setIsProfileLoading] = useState(true);
	const [loadErrorType, setLoadErrorType] = useState('');
	const [isRanksModalOpen, setIsRanksModalOpen] = useState(false)
	const [activeTab, setActiveTab] = useState('routines')
	const [profilePosts, setProfilePosts] = useState([]);
	const [loadingProfilePosts, setLoadingProfilePosts] = useState(false);
	const [isSocialModalOpen, setIsSocialModalOpen] = useState(false);
	const [socialModalType, setSocialModalType] = useState('followers');
	const [updatingFollowUserID, setUpdatingFollowUserID] = useState('');
	const [reloadTick, setReloadTick] = useState(0);
	const requestApi = useCallback((requestFactory) => requestWithApiFallback(API_URL, requestFactory), [API_URL]);

	const getProfileCacheKey = useCallback(() => (
		user?.id ? `profile-page-user:${user.id}` : ''
	), [user?.id]);

	const getCachedProfile = useCallback(() => {
		const key = getProfileCacheKey();
		if (!key) return null;
		try {
			const raw = window.localStorage.getItem(key);
			if (!raw) return null;
			const parsed = JSON.parse(raw);
			return parsed || null;
		} catch {
			return null;
		}
	}, [getProfileCacheKey]);

	const cacheProfile = useCallback((nextUser) => {
		const key = getProfileCacheKey();
		if (!key || !nextUser) return;
		try {
			window.localStorage.setItem(key, JSON.stringify(nextUser));
		} catch {}
	}, [getProfileCacheKey]);

	const getUser = useCallback(async () => {
		if (!user?.id) {
			setDbUser(null);
			setLoadErrorType('');
			setIsProfileLoading(false);
			return;
		}
		setIsProfileLoading(true);
		setLoadErrorType('');
		try {
			let response = null;
			let lastError = null;
			const retryDelays = [0, 400, 900];
			for (let i = 0; i < retryDelays.length; i += 1) {
				if (retryDelays[i] > 0) {
					await new Promise((resolve) => setTimeout(resolve, retryDelays[i]));
				}
				try {
					response = await requestApi((base) => axios.get(`${base}/users/${user.id}`));
					lastError = null;
					break;
				} catch (err) {
					lastError = err;
					if (err?.response?.status === 404) break;
				}
			}

			const normalized = normalizeUserPayload(response?.data);
			if (normalized) {
				setDbUser(normalized);
				cacheProfile(normalized);
				return;
			}

			if (lastError?.response?.status === 404) {
				setDbUser(null);
				setLoadErrorType('not-found');
				return;
			}

			const cached = getCachedProfile();
			if (cached) {
				setDbUser(cached);
			} else {
				setDbUser(null);
			}
			setLoadErrorType('temporary');
		} catch (err) {
			console.log("Error:", err.message);
			const cached = getCachedProfile();
			if (cached) {
				setDbUser(cached);
				setLoadErrorType('temporary');
			} else {
				setDbUser(null);
				setLoadErrorType('temporary');
			}
		} finally {
			setIsProfileLoading(false);
		}
	}, [cacheProfile, getCachedProfile, requestApi, user?.id]);

	const handleOpenRanksModal = () => {
		setIsRanksModalOpen(true)
	}

	const handleCloseRanksModal = () => {
		setIsRanksModalOpen(false)
	}

	const openSocialModal = (type) => {
		setSocialModalType(type);
		setIsSocialModalOpen(true);
	};

	const getSocialEntries = () => {
		if (!dbUser?.socials) return [];
		return socialModalType === 'followers'
			? (dbUser?.socials?.followers || [])
			: (dbUser?.socials?.followings || []);
	};

	const currentFollowingIDs = new Set((dbUser?.socials?.followings || []).map((entry) => entry.userID));

	const toggleFollowFromModal = async (targetUser) => {
		if (!user?.id || !dbUser || !targetUser?.userID || targetUser.userID === user.id) return;
		const isFollowing = currentFollowingIDs.has(targetUser.userID);
		setUpdatingFollowUserID(targetUser.userID);
		try {
			await requestApi((base) => axios.post(`${base}/users/${targetUser.userID}/${isFollowing ? 'unfollow' : 'follow'}`, {
				userID: user.id,
				username: user.username || dbUser.username || user.firstName || 'User',
				profileImageLink: user.profileImageUrl
			}));

			setDbUser((prev) => {
				if (!prev) return prev;
				const followers = [...(prev.socials?.followers || [])];
				const followings = [...(prev.socials?.followings || [])];
				const normalizedTarget = {
					userID: targetUser.userID,
					username: targetUser.username,
					profileImageLink: targetUser.profileImageLink
				};

				const nextFollowings = isFollowing
					? followings.filter((entry) => entry.userID !== targetUser.userID)
					: followings.some((entry) => entry.userID === targetUser.userID)
						? followings
						: [...followings, normalizedTarget];

				const nextFollowers = isFollowing
					? followers
					: followers.some((entry) => entry.userID === targetUser.userID)
						? followers
						: followers;

				return {
					...prev,
					socials: {
						...(prev.socials || {}),
						followers: nextFollowers,
						followings: nextFollowings
					}
				};
			});
		} catch (error) {
			console.error('Follow toggle error:', error.message);
		} finally {
			setUpdatingFollowUserID('');
		}
	};

	const fetchProfilePosts = useCallback(async () => {
		if (!user?.id) return;
		setLoadingProfilePosts(true);
		try {
			const response = await requestApi((base) => axios.get(`${base}/feed/user/${user.id}`));
			setProfilePosts(response.data.posts || []);
		} catch (error) {
			console.error('Profile posts error:', error.message);
		} finally {
			setLoadingProfilePosts(false);
		}
	}, [requestApi, user?.id]);

	useEffect(() => {
		if (!isLoaded) return;
		if (user?.id) {
			getUser();
		} else {
			setDbUser(null);
			setLoadErrorType('');
			setIsProfileLoading(false);
		}
	}, [getUser, isLoaded, reloadTick, user?.id]);

	useEffect(() => {
		if (activeTab === 'posts' && user?.id) {
			fetchProfilePosts();
		}
	}, [activeTab, fetchProfilePosts, user?.id]);

	useEffect(() => {
		const tab = location.state?.activeTab;
		if (tab && ['routines', 'meals', 'plans', 'saved', 'posts'].includes(tab)) {
			setActiveTab(tab);
		}
	}, [location.state?.activeTab]);

	const followerEntries = dbUser?.socials?.followers || [];
	const followingEntries = dbUser?.socials?.followings || [];
	const userRoutines = dbUser?.stryds?.routines || [];
	const joinedRoutines = dbUser?.stryds?.joinedRoutines || [];
	const generatedRoutines = userRoutines.filter(
		(routine) => normalizeRoutineCreationType(routine?.routineCreationType) === 'generated'
	);
	const createdRoutines = userRoutines.filter(
		(routine) => normalizeRoutineCreationType(routine?.routineCreationType) === 'created'
	);
	const routineBookmarks = (dbUser?.bookmarks || []).filter((bookmark) => bookmark?.type === 'routine');
	const workoutPlans = dbUser?.workoutPlans || [];
	const workoutInterests = dbUser?.interests?.workoutInterests || [];
	const displayUsername = formatDisplayName(user?.username || dbUser?.username || user?.firstName || 'Athlete');
	const profileImageSrc = user?.profileImageUrl || dbUser?.profileImageLink || '';

	if (!isLoaded || isProfileLoading) {
		return <AppLoader message='Loading your profile...' />;
	}

	if (!user) {
		return (
			<div className={style.ProfilePage}>
				<div className={style.EmptyStateCard}>
					<span className={style.EmptyText}>Please log in to view your profile.</span>
					<PrimaryButton
						text='Go to login'
						click={() => navigate('/login?redirectTo=/profile-page')}
						width='180px'
					/>
				</div>
			</div>
		);
	}

	if (!dbUser) {
		return (
			<div className={style.ProfilePage}>
				<div className={style.EmptyStateCard}>
					<span className={style.EmptyText}>
						{loadErrorType === 'not-found'
							? 'Your profile was not found.'
							: 'We could not load your profile details right now.'}
					</span>
					<PrimaryButton text='Retry' click={() => setReloadTick((tick) => tick + 1)} width='140px' />
				</div>
			</div>
		);
	}

	// console.log(clerkClient)
	return (
		<div className={style.ProfilePage}>
			{loadErrorType === 'temporary' && (
				<div className={style.ProfileNotice}>
					Using cached profile data. Pull to refresh or tap retry if anything looks outdated.
				</div>
			)}

			{isRanksModalOpen && (
				<RanksModal
					onClose={handleCloseRanksModal}
					ranks={rankData}
					currentXP={dbUser?.totalXP || 0}
				/>
			)}
			<SocialConnectionsModal
				isOpen={isSocialModalOpen}
				title={socialModalType === 'followers' ? 'Followers' : 'Following'}
				users={getSocialEntries()}
				onClose={() => setIsSocialModalOpen(false)}
				currentUserID={user?.id}
				currentFollowingIDs={currentFollowingIDs}
				onToggleFollow={toggleFollowFromModal}
				isUpdatingUserID={updatingFollowUserID}
			/>

			<>
				<div className={style.ProfileInfoWrapper}>
					<div className={style.ProfileUserStats}>
						<div>
							<div className={style.ProfileInfo}>
								<img
									className={style.ProfilePicture}
									src={profileImageSrc}
									alt={`${displayUsername} profile`} />


								<div className={style.ProfileInfoDetails}>
									<div className={style.ProfileInfoText}>
										<span className={style.ProfileInfoUsername}>
											{displayUsername}
											<VerifiedBadge userPlan={dbUser.userPlan} totalXP={dbUser.totalXP} size='large' />
											{!checkSubscription(dbUser) && <span className={style.UsernameRankBadge}>{findUserRank(dbUser.totalXP)?.emoji}</span>}
										</span>

										<button
											type="button"
											className={style.ProfileRank}
											onClick={handleOpenRanksModal}
										>
											{findUserRank(dbUser.totalXP)?.rank}
										</button>
									</div>


									<div className={style.ProfileAttributesWrapper}>
										<button
											type='button'
											className={`${style.ProfileAttribute} ${style.ProfileStatButton}`}
											onClick={() => openSocialModal('followers')}
										>
											<span className={style.ProfileAttributeHeading}>
												Followers
											</span>

											<span className={style.ProfileCount}>
												{followerEntries.length}
											</span>
										</button>

										<button
											type='button'
											className={`${style.ProfileAttribute} ${style.ProfileStatButton}`}
											onClick={() => openSocialModal('following')}
										>
											<span className={style.ProfileAttributeHeading}>
												Following
											</span>

											<span className={style.ProfileCount}>
												{followingEntries.length}
											</span>
										</button>

										<div className={style.ProfileAttribute}>
											<span className={style.ProfileAttributeHeading}>
												Total XP
											</span>

											<span className={style.ProfileCount}>
												{dbUser.totalXP}
											</span>
										</div>
									</div>
								</div>

							</div>
						</div>

						<div>
							<div>
								<span>
									<div className={style.UserInterests}>
										{workoutInterests.length > 0 ? (
											workoutInterests.map((interest) => (
												<Tag key={interest} label={interest} />
											))
										) : (
											<span className={style.EmptyText}>
												No workout interests
											</span>
										)}
									</div>
								</span>
							</div>
						</div>
					</div>
				</div>

				{dbUser?.lastWorkout?.routineName && dbUser?.lastWorkout?.routineCreatorID && (
					<Link
						to={`/stryd/${dbUser.lastWorkout.routineCreatorID}/${encodeURIComponent(dbUser.lastWorkout.routineName)}`}
						className={style.PickUpCard}
					>
						<div className={style.PickUpIcon}>
							<MdPlayArrow />
						</div>
						<div className={style.PickUpContent}>
							<span className={style.PickUpLabel}>Pick up where you left off</span>
							<span className={style.PickUpRoutine}>{dbUser.lastWorkout.routineName}</span>
						</div>
						<MdArrowForward className={style.PickUpChevron} />
					</Link>
				)}

				<div className={style.UserRoutines}>
					<div className={style.TabsContainer}>
						<button
							className={`${style.Tab} ${activeTab === 'routines' ? style.ActiveTab : ''}`}
							onClick={() => setActiveTab('routines')}
						>
							Routines
						</button>
						<button
							className={`${style.Tab} ${activeTab === 'meals' ? style.ActiveTab : ''}`}
							onClick={() => setActiveTab('meals')}
						>
							Meals
						</button>
						<button
							className={`${style.Tab} ${activeTab === 'plans' ? style.ActiveTab : ''}`}
							onClick={() => setActiveTab('plans')}
						>
							Workout Plans
						</button>
						<button
							className={`${style.Tab} ${activeTab === 'saved' ? style.ActiveTab : ''}`}
							onClick={() => setActiveTab('saved')}
						>
							Saved
						</button>
						<button
							className={`${style.Tab} ${activeTab === 'posts' ? style.ActiveTab : ''}`}
							onClick={() => setActiveTab('posts')}
						>
							Posts
						</button>
					</div>

					{activeTab === 'routines' ? (
					<>
					{/* Generated Routines */}
					<div className={style.CardsWrapper}>
						<span className='SectionTitle'>Generated Routines</span>
						{generatedRoutines.length > 0 ? (
							<div className={style.CardsContainer}>
								{generatedRoutines.map((routine, index) => (
									<Link
										key={routine._id || `${routine.routineName}-${index}`}
										to={`/stryd/${user.id}/${encodeURIComponent(routine.routineName)}`}
									>
										<StrydCardSmall
											name={routine.routineName}
											time={routine.estimatedTimeMinutes}
											creator={user.username || dbUser.username || 'You'}
											userID={user.id}
											type={normalizeRoutineCreationType(routine.routineCreationType)}
										/>
									</Link>
								))}
							</div>
						) : (
							<span className={style.EmptyText}>No generated routines yet.</span>
						)}
					</div>

					{/* Created Routines */}
					<div className={style.CardsWrapper}>
						<span className='SectionTitle'>Created Routines</span>
						{createdRoutines.length > 0 ? (
							<div className={style.CardsContainer}>
								{createdRoutines.map((routine, index) => (
									<Link
										key={routine._id || `${routine.routineName}-${index}`}
										to={`/stryd/${user.id}/${encodeURIComponent(routine.routineName)}`}
									>
										<StrydCardSmall
											name={routine.routineName}
											time={routine.estimatedTimeMinutes}
											creator={user.username || dbUser.username || 'You'}
											userID={user.id}
											type={normalizeRoutineCreationType(routine.routineCreationType)}
										/>
									</Link>
								))}
							</div>
						) : (
							<span className={style.EmptyText}>No created routines yet.</span>
						)}
					</div>

					{/* Joined Routines */}
					<div className={style.CardsWrapper}>
						<span className='SectionTitle'>Joined Routines</span>
						{joinedRoutines.length > 0 ? (
							<div className={style.CardsContainer}>
								{joinedRoutines.map((routine, index) => (
									<Link
										key={routine._id || `${routine.routineName}-${index}`}
										to={`/stryd/${routine.routineCreatorUserID}/${encodeURIComponent(routine.routineName)}`}
									>
										<StrydCardSmall
											name={routine.routineName}
											time={routine.estimatedTimeMinutes}
											creator={routine.routineCreator}
										/>
									</Link>
								))}
							</div>
						) : (
							<span className={style.EmptyText}>No joined routines yet.</span>
						)}
					</div>
					</>
					) : activeTab === 'meals' ? (
						<MealsTabSection meals={dbUser?.meals} navigate={navigate} />
					) : activeTab === 'plans' ? (
						<PlansTabSection plans={workoutPlans} navigate={navigate} userID={user?.id} />
					) : activeTab === 'posts' ? (
						<div className={style.MealsTabContent}>
							<span className='SectionTitle'>Your Posts</span>
							{loadingProfilePosts ? (
								<p className={style.EmptyText}>Loading posts...</p>
							) : profilePosts.length > 0 ? (
								<div className={style.PostsList}>
									{profilePosts.map((post) => (
										<Link key={post._id} to={`/feed/post/${post._id}`} className={style.PostPreviewLink}>
											<div className={style.PostPreviewCard}>
												<p className={style.PostPreviewText}>{post.content || 'Shared an update'}</p>
												<div className={style.PostPreviewMeta}>
													<span>{new Date(post.createdAt).toLocaleDateString()}</span>
													<span>{post.likes?.length || 0} {(post.likes?.length || 0) === 1 ? 'like' : 'likes'}</span>
													<span>{post.comments?.length || 0} {(post.comments?.length || 0) === 1 ? 'reply' : 'replies'}</span>
												</div>
											</div>
										</Link>
									))}
								</div>
							) : (
								<p className={style.EmptyText}>No posts yet.</p>
							)}
						</div>
					) : (
						<div className={style.MealsTabContent}>
							<span className='SectionTitle'>Saved Routines</span>
							{routineBookmarks.length > 0 ? (
								<div className={style.CardsContainer}>
									{routineBookmarks.map(bookmark => {
											const [creatorID, savedRoutineName] = (bookmark.refID || '').split(':');
											if (!creatorID || !savedRoutineName) {
												return null;
											}
											return (
												<Link
													key={bookmark._id}
													to={`/stryd/${creatorID}/${encodeURIComponent(savedRoutineName)}`}
												>
													<StrydCardSmall
														name={savedRoutineName}
														time={'--'}
														creator={bookmark.refName || 'Saved'}
													/>
												</Link>
											);
										})}
								</div>
							) : (
								<p className={style.EmptyText}>No saved routines yet. Save routines from their detail page.</p>
							)}
						</div>
					)}
				</div>

			</>
		</div >
	)
}

function PlansTabSection({ plans, navigate, userID }) {
	if (!plans || plans.length === 0) {
		return (
			<div className={style.MealsTabContent}>
				<span className='SectionTitle'>Workout Plans</span>
				<p className={style.EmptyText}>Multi-day workout plans (with days and weeks) live here. Create one from the plan builder.</p>
				<PrimaryButton
					text="Build a plan"
					click={() => navigate('/plan-builder')}
					width='180px'
				/>
			</div>
		);
	}

	return (
		<div className={style.MealsTabContent}>
			<span className='SectionTitle'>Workout Plans</span>
			<div className={style.MealPlansList}>
				{plans.map((plan) => {
					const planId = plan._id;
					const workoutDays = (plan.days || []).filter((d) => !d.isRestDay);
					const totalDays = plan.planDuration || (plan.days || []).length;
					const totalWeeks = Math.max(1, Math.ceil(totalDays / 7));
					return (
						<button
							key={planId}
							type="button"
							className={style.PlanCardButton}
							onClick={() => navigate(`/plan/${planId}`)}
						>
							<div className={style.PlanCardIcon}>
								<MdCalendarMonth />
							</div>
							<div className={style.PlanCardContent}>
								<span className={style.MealPlanTitle}>{plan.planName}</span>
								<span className={style.PlanCardMeta}>
									{totalDays}-day plan · {totalWeeks} week{totalWeeks > 1 ? 's' : ''} · {workoutDays.length} workout days
									{plan.createdDate && ` · ${plan.createdDate}`}
								</span>
							</div>
							<MdExpandMore className={style.PlanCardChevron} />
						</button>
					);
				})}
			</div>
		</div>
	);
}

const MEAL_EMOJI = {
	breakfast: '🌅',
	lunch: '☀️',
	dinner: '🌙',
	snack: '🍎',
	snacks: '🍎'
};

function MealsTabSection({ meals, navigate }) {
	const [expandedPlan, setExpandedPlan] = useState(null);
	const [expandedDay, setExpandedDay] = useState(null);

	if (!meals || meals.length === 0) {
		return (
			<div className={style.MealsTabContent}>
				<span className='SectionTitle'>Meals</span>
				<p className={style.EmptyText}>Generate personalized meal plans tailored to your goals.</p>
				<PrimaryButton
					text="Open Meal Plans"
					click={() => navigate('/meals')}
					width='180px'
				/>
			</div>
		);
	}

	return (
		<div className={style.MealsTabContent}>
			<span className='SectionTitle'>Meals</span>
			<div className={style.MealPlansList}>
				{meals.map((mealPlan, idx) => {
					const isOpen = expandedPlan === idx;
					return (
						<div key={idx} className={style.MealPlanCard}>
							<button
								type="button"
								className={style.MealPlanHeader}
								onClick={() => {
									setExpandedPlan(isOpen ? null : idx);
									setExpandedDay(null);
								}}
							>
								<div className={style.MealPlanHeaderText}>
									<span className={style.MealPlanTitle}>{mealPlan.planName}</span>
									<span className={style.MealPlanDate}>{mealPlan.createdDate}</span>
								</div>
								<span className={style.MealPlanToggle}>
									{isOpen ? <MdExpandLess /> : <MdExpandMore />}
								</span>
							</button>

							{isOpen && mealPlan.days && (
								<div className={style.MealDaysGrid}>
									{mealPlan.days.map((day, dayIdx) => {
										const mealEntries = getMealEntriesForDay(day);
										const isDayOpen = expandedDay === dayIdx;
										return (
											<div key={dayIdx} className={style.MealDayCard}>
												<button
													type="button"
													className={style.MealDayHeader}
													onClick={() => setExpandedDay(isDayOpen ? null : dayIdx)}
												>
													<span className={style.MealDayLabel}>Day {dayIdx + 1}</span>
													<span className={style.MealDayCount}>
														{mealEntries.length} meals
													</span>
												</button>

												{isDayOpen && (
													<div className={style.MealItemsGrid}>
														{mealEntries.map(({ mealType, name, calories }) => (
															<div key={mealType} className={style.MealItemCard}>
																<span className={style.MealItemEmoji}>
																	{MEAL_EMOJI[mealType.toLowerCase()] || '🍽️'}
																</span>
																<div className={style.MealItemInfo}>
																	<span className={style.MealType}>{mealType}</span>
																	<span className={style.MealName}>{name}</span>
																	{calories !== null && (
																		<span className={style.MealDayCount}>{calories} cal</span>
																	)}
																</div>
															</div>
														))}
													</div>
												)}
											</div>
										);
									})}
								</div>
							)}
						</div>
					);
				})}
			</div>
		</div>
	);
}

export default ProfilePage;